import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import {
  FileField,
  Form,
  FormError,
  PageHeader,
  Panel,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { PATHS } from '../../routes/paths';
import { useUploadCertificate } from '../queries';
import { CERTIFICATES_COPY, refusalMessage } from './certificates';

/**
 * Installing an A1 certificate.
 *
 * Its own screen so the list next door is the reading -- what we can sign with,
 * and until when -- without a file picker open over it. The list is what
 * somebody comes to that menu entry for; this is what they come to once a year.
 *
 * **The password is write-only, and the screen says so before it is typed.** It
 * is sent in the upload and never comes back: no endpoint returns it, and the
 * response type has no field that could. The hint under the field says to keep
 * it in the team's password vault, because the day the certificate has to be
 * reinstalled is the day somebody discovers this screen cannot help.
 *
 * On success it returns to the list, where the installed certificate -- and the
 * one it superseded, still listed -- are what the server now reports.
 */
export function NewCertificatePage() {
  const upload = useUploadCertificate();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');

  const uploadErrors = fieldErrors(upload.error);

  const submit = () => {
    if (file === null) {
      return;
    }
    upload.mutate(
      { file, password },
      {
        onSuccess: (installed) => {
          notify(
            'success',
            installed.supersedes_certificate_id !== null &&
              installed.supersedes_certificate_id !== undefined
              ? CERTIFICATES_COPY.replaced
              : CERTIFICATES_COPY.installed,
          );
          void navigate(PATHS.staffCertificates);
        },
      },
    );
  };

  return (
    <div className="page">
      <PageHeader
        title={CERTIFICATES_COPY.uploadTitle}
        description={CERTIFICATES_COPY.uploadDescription}
        actions={
          <Link className="button button--ghost" to={PATHS.staffCertificates}>
            {CERTIFICATES_COPY.back}
          </Link>
        }
      />

      <Panel
        title={CERTIFICATES_COPY.uploadTitle}
        description={CERTIFICATES_COPY.uploadDescription}
      >
        <Form onSubmit={submit} aria-label={CERTIFICATES_COPY.uploadTitle}>
          {/* The server's four refusals are four different things to go and
              do, so each gets its own sentence; anything else falls back to
              the code's own pt-BR wording. */}
          <FormError
            error={upload.error}
            {...(refusalMessage(upload.error) !== null && {
              message: refusalMessage(upload.error) ?? undefined,
            })}
          />
          <FileField
            label={CERTIFICATES_COPY.file}
            onChange={setFile}
            accept=".pfx,.p12,application/x-pkcs12"
            hint={CERTIFICATES_COPY.fileHint}
            required
            {...(uploadErrors['file'] !== undefined && { error: uploadErrors['file'] })}
          />
          <TextField
            label={CERTIFICATES_COPY.password}
            value={password}
            onChange={setPassword}
            type="password"
            required
            // No autofill: this is not the person's own password, and a browser
            // offering to remember it would put it somewhere this application
            // has spent a lot of effort keeping things out of.
            autoComplete="off"
            hint={CERTIFICATES_COPY.passwordHint}
            {...(uploadErrors['password'] !== undefined && { error: uploadErrors['password'] })}
          />
          <div className="form__actions">
            <SubmitButton pending={upload.isPending} pendingLabel={CERTIFICATES_COPY.submitting}>
              {CERTIFICATES_COPY.submit}
            </SubmitButton>
            <Link className="button button--ghost" to={PATHS.staffCertificates}>
              {CERTIFICATES_COPY.cancel}
            </Link>
          </div>
        </Form>
      </Panel>
    </div>
  );
}
